import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import {
    Rocket,
    Star,
    Lightning,
    CheckCircle,
    ArrowRight,
    Crown,
} from "@phosphor-icons/react";

const INR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

// Reusable pricing grid for the 3 candidate boost tiers.
// Used on the public landing page AND the authenticated /candidate/boost page.
export default function BoostPlans({
    variant = "public", // "public" | "candidate"
    currentPlanId = null,
    onCheckoutStart = null,
    onPurchased = null,
}) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [buying, setBuying] = useState(null);

    useEffect(() => {
        api
            .get("/candidate/boost/plans")
            .then((r) => {
                setPlans(r.data.plans || []);
                setError(null);
            })
            .catch((e) => {
                setPlans([]);
                setError(e?.response?.data?.detail || "Couldn't load pricing right now.");
            })
            .finally(() => setLoading(false));
    }, []);

    const buy = async (planId) => {
        if (!user) {
            navigate(`/candidate/signup?after=/candidate/boost`);
            return;
        }
        if (user.role !== "candidate") {
            // Wrong-role user (recruiter / admin / partner) — route them to
            // the candidate signup page so they can create a separate
            // candidate account instead of hitting a dead-end error toast.
            toast.info(
                "Boost is for candidate accounts — creating one for you now.",
            );
            navigate(`/candidate/signup?after=/candidate/boost`);
            return;
        }
        try {
            setBuying(planId);
            onCheckoutStart?.(planId);
            const { data: order } = await api.post("/candidate/boost/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            const resp = await openRazorpayCheckout({
                order,
                name: "NaukriDaddy · Boost",
                description: `${order.plan_name} · Candidate Boost`,
                prefill: { email: user?.email, name: user?.name },
            });
            await api.post("/candidate/boost/verify", resp);
            toast.success("🚀 Boost activated! Your alerts start rolling in.");
            onPurchased?.(planId);
        } catch (e) {
            if (e?.code === "dismissed") {
                toast.info("Checkout closed — no charges made.");
            } else if (e?.code === "failed") {
                toast.error(e.reason || "Payment failed. Please retry.");
            } else {
                toast.error(e?.response?.data?.detail || "Could not complete payment — try again.");
            }
        } finally {
            setBuying(null);
        }
    };

    if (loading) {
        // Skeleton looks like an actual pricing card so users understand it's
        // loading (not a broken empty box). Each skeleton includes a subtle
        // "Loading pricing…" label + shimmer.
        return (
            <div
                data-testid="boost-plans-loading"
                className="grid gap-3 sm:gap-4 sm:grid-cols-3"
            >
                {[0, 1, 2].map((n) => (
                    <div
                        key={n}
                        className="relative h-[420px] border border-[#E5E5E5] bg-white p-5 sm:p-6 overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#F5F5F5]/60 to-transparent animate-shimmer" />
                        <div className="relative">
                            <div className="h-3 w-24 bg-[#F5F5F5] mb-4" />
                            <div className="h-4 w-40 bg-[#F5F5F5] mb-5" />
                            <div className="h-8 w-28 bg-[#F5F5F5] mb-6" />
                            <div className="space-y-2">
                                {[0, 1, 2, 3].map((k) => (
                                    <div key={k} className="h-3 w-full bg-[#F5F5F5]" />
                                ))}
                            </div>
                            <p className="absolute bottom-0 left-0 right-0 text-center text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                Loading pricing…
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Graceful fallback if the API failed AND we have zero plans to render.
    if (!plans.length) {
        return (
            <div
                data-testid="boost-plans-empty"
                className="border border-[#E5E5E5] bg-white p-6 sm:p-8 text-center"
            >
                <Rocket size={22} weight="fill" className="text-[#A3A3A3] mx-auto mb-2" />
                <p className="font-heading text-lg font-black text-ink mb-1">
                    Pricing is temporarily unavailable
                </p>
                <p className="text-xs text-[#525252] mb-4">
                    {error || "We're having trouble loading Boost plans."}
                </p>
                <button
                    onClick={() => window.location.reload()}
                    data-testid="boost-plans-retry"
                    className="text-[11px] font-black uppercase tracking-wider bg-ink text-white px-4 py-2 hover:bg-cobalt"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div data-testid="boost-plans-grid" className="grid gap-3 sm:gap-4 sm:grid-cols-3">
            {plans.map((p) => {
                const featured = !!p.featured;
                const isCurrent = currentPlanId === p.id;
                const isBuying = buying === p.id;
                return (
                    <div
                        key={p.id}
                        data-testid={`boost-plan-${p.id}`}
                        className={`relative flex flex-col p-5 sm:p-6 border transition-all ${
                            featured
                                ? "bg-ink text-white border-splash shadow-[0_0_0_2px_rgba(91,226,235,0.35)]"
                                : "bg-white text-ink border-[#E5E5E5] hover:border-cobalt"
                        }`}
                    >
                        {featured && (
                            <span
                                data-testid={`boost-plan-featured-${p.id}`}
                                className="absolute -top-2 right-4 bg-splash text-ink text-[9px] font-black uppercase tracking-[0.2em] px-2 py-0.5"
                            >
                                Most popular
                            </span>
                        )}

                        <div className="flex items-center gap-2 mb-3">
                            {p.id === "boost_12m" ? (
                                <Crown
                                    size={18}
                                    weight="fill"
                                    className={featured ? "text-splash" : "text-[#F59E0B]"}
                                />
                            ) : p.id === "boost_6m" ? (
                                <Star
                                    size={18}
                                    weight="fill"
                                    className={featured ? "text-splash" : "text-cobalt"}
                                />
                            ) : (
                                <Rocket
                                    size={18}
                                    weight="fill"
                                    className={featured ? "text-splash" : "text-cobalt"}
                                />
                            )}
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                                {p.name}
                            </span>
                        </div>

                        <p
                            className={`text-sm mb-4 ${
                                featured ? "text-white/70" : "text-[#525252]"
                            }`}
                        >
                            {p.tagline}
                        </p>

                        <div className="mb-5">
                            <span className="font-heading text-4xl font-black tracking-tighter">
                                {INR(p.price)}
                            </span>
                            <span
                                className={`ml-2 text-xs font-bold uppercase tracking-wider ${
                                    featured ? "text-white/60" : "text-[#A3A3A3]"
                                }`}
                            >
                                /{p.period_days === 90
                                    ? "3 months"
                                    : p.period_days === 180
                                    ? "6 months"
                                    : "year"}
                            </span>
                        </div>

                        <div
                            className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${
                                featured ? "text-splash" : "text-cobalt"
                            }`}
                        >
                            {p.alerts_limit === -1
                                ? "Unlimited alerts"
                                : `${p.alerts_limit} job alerts`}
                        </div>

                        <ul className="space-y-2 mb-6 flex-1">
                            {(p.features || []).map((f, i) => (
                                <li
                                    key={i}
                                    className={`flex gap-2 text-xs ${
                                        featured ? "text-white/85" : "text-[#262626]"
                                    }`}
                                >
                                    <CheckCircle
                                        size={14}
                                        weight="fill"
                                        className={
                                            featured ? "text-splash flex-shrink-0" : "text-cobalt flex-shrink-0"
                                        }
                                    />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>

                        {isCurrent ? (
                            <div
                                data-testid={`boost-current-${p.id}`}
                                className={`text-center py-3 text-[11px] font-black uppercase tracking-[0.2em] ${
                                    featured
                                        ? "bg-splash text-ink"
                                        : "bg-[#F5F5F5] text-[#525252]"
                                }`}
                            >
                                Your current plan
                            </div>
                        ) : (
                            <button
                                onClick={() => buy(p.id)}
                                disabled={isBuying}
                                data-testid={`boost-buy-${p.id}`}
                                className={`w-full py-3 text-[11px] font-black uppercase tracking-[0.2em] transition-colors inline-flex items-center justify-center gap-2 ${
                                    featured
                                        ? "btn-gradient text-ink"
                                        : "bg-ink hover:bg-cobalt text-white"
                                } ${isBuying ? "opacity-60 cursor-wait" : ""}`}
                            >
                                {isBuying ? (
                                    "Opening…"
                                ) : (
                                    <>
                                        {variant === "public" && (!user || user.role !== "candidate")
                                            ? "Sign up & boost"
                                            : "Get boosted"}
                                        <ArrowRight size={12} weight="bold" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
